<template>
  <div v-if="isConsentBannerEnabled && loggedInUser?.id" class="fixed inset-x-0 bottom-0 z-50 p-2">
    <transition name="slide-up">
      <Message v-if="!analyticsConsentGiven" severity="warn" has-border>
        <div>
          <div class="type-h6 mb-2">
            {{ t('analytics.banner.title') }}
          </div>
          <div class="type-body-2 mt-1">
            <span>{{ t('analytics.banner.content') }}</span>
            <span
              class="inline cursor-pointer font-medium underline underline-offset-2"
              @click="viewDocument"
            >
              {{ t('analytics.banner.link') }}
            </span>
          </div>
        </div>
        <template #action>
          <div class="hidden lg:flex">
            <Button
              data-cy="analytics-consent-dismiss"
              size="sm"
              variant="tertiary"
              @click="handleDismissBanner"
            >
              <template #left-icon>
                <XMarkIcon class="h-5 w-5" />
                {{ t('general.dismiss') }}
              </template>
            </Button>
          </div>
          <div class="flex lg:hidden">
            <Button data-cy="analytics-consent-dismiss" size="sm" @click="handleDismissBanner">
              {{ t('analytics.banner.dismiss') }}
            </Button>
          </div>
        </template>
      </Message>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';

import i18n from '@/plugins/vue-i18n';
import { isConsentBannerEnabled, isPostHogEnabled } from '@/utils/flags';
import { LEGAL_DOCS_LINK } from '@/common/legal';
import {
  analyticsService,
  getScopedConsentKey,
  hasAcceptedCurrentConsent,
} from '@/plugins/post-hog';

import { useAuthStore } from '@/stores/auth.store';

import { Button, Message } from '@boqnet/ui';
import { XMarkIcon } from '@heroicons/vue/24/outline';

const { t } = i18n.global;

const authStore = useAuthStore();
const { loggedInUser } = storeToRefs(authStore);

// Default to "consented" so the banner never flashes before the check resolves.
const analyticsConsentGiven = ref(true);

const handleDismissBanner = () => {
  analyticsService.setConsent('true');
  analyticsConsentGiven.value = true;
};

const viewDocument = () => {
  window.open(LEGAL_DOCS_LINK, '_blank', 'noopener');
};

// Drive the consent check off login rather than this component's mount: the
// banner lives in App.vue and mounts once at boot (before login), so an
// onMounted-only check would run with no user and never re-run after login.
// `immediate` covers the already-logged-in (page refresh) case.
watch(
  () => loggedInUser.value?.id,
  (userId) => {
    if (!userId) {
      analyticsConsentGiven.value = true;
      return;
    }

    // Brief delay so PostHog has initialised/identified before we read or sync consent.
    setTimeout(async () => {
      const id = loggedInUser.value?.id;
      if (!id) return;

      const hasLocalRecord = !!localStorage.getItem(getScopedConsentKey(id));
      analyticsConsentGiven.value = hasAcceptedCurrentConsent(id);

      if (
        !analyticsConsentGiven.value &&
        isPostHogEnabled &&
        !authStore.analyticsSyncCheckedOnLogin
      ) {
        analyticsConsentGiven.value = await analyticsService.syncConsentFromRemote(
          authStore.analyticsSyncCheckedOnLogin,
        );
      } else if (hasLocalRecord) {
        analyticsService.identify();
      }
    }, 1000);
  },
  { immediate: true },
);
</script>

<style scoped>
@reference "tailwindcss";

/* Banner Slide up and collapse */
.slide-up-leave-active {
  transition:
    max-height 300ms ease-in,
    opacity 200ms ease-in,
    transform 300ms ease-in;
  overflow: hidden;
}

.slide-up-leave-from {
  max-height: 200px; /* must be >= banner height */
  opacity: 1;
  transform: translateY(0);
}

.slide-up-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-8px);
}
</style>
